import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { businessServices } from "@/data/servicesData";
import { useEffect } from "react";
import SEO, { breadcrumbSchema } from "@/components/SEO";

export default function ForBusinesses() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="businesses-page">
      <SEO
        title="Crypto Business Consulting | Licensing, Compliance, Development, Hiring"
        description="End-to-end crypto business consultancy in India. FIU-IND registration, PMLA compliance, blockchain development, Web3 hiring, project management, and token launch advisory. Trusted by startups and enterprises."
        path="/businesses"
        keywords={[
          "crypto business consulting India",
          "FIU-IND registration consultant",
          "PMLA compliance crypto",
          "blockchain development consultancy",
          "web3 hiring India",
          "token launch advisory",
          "VDA compliance India",
        ]}
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "For Businesses", path: "/businesses" },
        ])}
      />
      {/* Hero */}
      <section className="bg-[#21324D] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-end">
            <div className="lg:col-span-7">
              <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">For Businesses</p>
              <h1
                className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F2] leading-none tracking-tight mb-8"
                data-testid="businesses-heading"
              >
                Build, launch, and scale your crypto business
              </h1>
              <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed max-w-xl">
                FIU-IND registration, PMLA compliance, blockchain development, Web3 hiring, and project management. BitGenix provides the end-to-end business consulting that crypto ventures need to succeed in India and globally.
              </p>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F5F5F2]/[0.06] border border-[#F5F5F2]/[0.08] p-5">
                  <p className="font-heading font-semibold text-3xl text-[#A7A55C]">4</p>
                  <p className="font-body text-xs text-[#F5F5F2]/50 uppercase tracking-[0.1em] mt-1">Industry Awards</p>
                </div>
                <div className="bg-[#F5F5F2]/[0.06] border border-[#F5F5F2]/[0.08] p-5">
                  <p className="font-heading font-semibold text-3xl text-[#A7A55C]">100%</p>
                  <p className="font-body text-xs text-[#F5F5F2]/50 uppercase tracking-[0.1em] mt-1">Crypto-Focused</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-14">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Our Services for Businesses</p>
            <h2 className="font-heading font-semibold text-3xl text-[#21324D] tracking-tight leading-tight">
              Crypto and blockchain consulting for every stage of growth
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businessServices.map((service) => (
              <Link
                to={`/services/${service.slug}`}
                key={service.slug}
                className="group bg-white border border-[#383A46]/10 p-8 sm:p-10 rounded-none transition-all duration-300 hover:-translate-y-1 hover:border-[#A7A55C] hover:shadow-[0_16px_48px_-12px_rgba(33,50,77,0.12)]"
                data-testid={`business-service-${service.slug}`}
              >
                <div className="bg-[#21324D] w-12 h-12 flex items-center justify-center mb-6">
                  <service.icon size={22} weight="duotone" className="text-[#A7A55C]" />
                </div>
                <h3 className="font-body font-medium text-lg text-[#21324D] mb-3 group-hover:text-[#A7A55C] transition-colors">
                  {service.title}
                </h3>
                <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-6">
                  {service.shortDesc}
                </p>
                <span className="font-body font-semibold text-sm text-[#A7A55C] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">
                  Learn more <ArrowRight size={14} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why BitGenix for Business */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Why BitGenix</p>
              <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
                Why choose a specialized crypto business consultant?
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="font-body text-base text-[#383A46] leading-[1.8] mb-6">
                Building a crypto business in India means navigating FIU-IND registration, PMLA compliance, SEBI monitoring, and constantly evolving regulatory requirements. On the technology side, blockchain development demands specialized skills that most traditional development shops simply do not have.
              </p>
              <p className="font-body text-base text-[#383A46] leading-[1.8]">
                BitGenix brings all of this under one roof. Our consulting team includes regulatory experts who have successfully guided dozens of crypto businesses through FIU-IND registration, blockchain architects who have built and audited production-grade smart contracts, and project managers who understand the unique rhythm of crypto product development. Instead of coordinating between five different vendors, you work with one team that understands the full picture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#152F2C]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#F5F5F2] tracking-tight leading-tight mb-6">
            Ready to build something in crypto?
          </h2>
          <p className="font-body text-base text-[#F5F5F2]/65 max-w-xl mx-auto mb-10">
            Tell us about your project or business challenge. We will design a consulting plan that fits your needs and timeline.
          </p>
          <Link
            to="/contact"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-all duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2 hover:gap-3"
            data-testid="businesses-cta-button"
          >
            Book a Consultation <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
